// API Configuration and Core Utilities

// Use relative URL in development (uses Vite proxy) or absolute URL from env
const getApiBaseUrl = () => {
    // If VITE_API_URL is set, use it
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }
    // In development, use relative URL to leverage Vite proxy
    if (import.meta.env.DEV) {
        return '/api';
    }
    // In production, default to localhost:8000 (Django default)
    return 'http://localhost:8000/api';
};

export const API_BASE_URL = getApiBaseUrl();
export const API_ORIGIN = API_BASE_URL.replace(/\/?api$/, '');

// Log API configuration only in development
if (import.meta.env.DEV) {
    console.log('🔌 API Configuration:', {
        mode: import.meta.env.MODE,
        apiBaseUrl: API_BASE_URL,
        apiOrigin: API_ORIGIN,
        viteApiUrl: import.meta.env.VITE_API_URL
    });
}

// Helper function to get auth token
export const getAuthToken = () => {
    return localStorage.getItem('authToken');
};

// Helper function to create headers
export const createHeaders = (includeAuth = true) => {
    const headers = {
        'Content-Type': 'application/json'
    };

    if (includeAuth) {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    return headers;
};

// --- Simple Client-Side Cache ---
export const CACHE_STORE = new Map();
export const DEFAULT_TTL = 10 * 60 * 1000; // 10 minutes

// Request deduplication - prevents duplicate concurrent requests
const pendingRequests = new Map();

/**
 * Wraps an API promise with caching and request deduplication
 * @param {string} key - Unique cache key
 * @param {Function} fetcher - Function that returns a promise
 * @param {Object} options - Cache options
 * @param {number} options.ttl - Time to live in ms
 * @param {boolean} options.persist - Whether to persist in localStorage
 * @param {string} options.version - Cache version for invalidation
 */
export const withCache = async (key, fetcher, options = {}) => {
    const { ttl = DEFAULT_TTL, persist = false, version = '1.0' } = typeof options === 'number' ? { ttl: options } : options;
    const storageKey = `pm_cache_${key}`;

    // 1. Check in-memory cache
    let cached = CACHE_STORE.get(key);

    // 2. Check localStorage if enabled and not in memory
    if (!cached && persist) {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.version === version) {
                    cached = parsed;
                    // Populate in-memory cache for faster subsequent access
                    CACHE_STORE.set(key, cached);
                }
            }
        } catch (e) {
            console.warn('Cache read error:', e);
        }
    }

    // 3. Validate cache
    if (cached && (Date.now() - cached.timestamp < ttl)) {
        if (import.meta.env.DEV) {
            console.log(`⚡ Cache hit: ${key}${persist ? ' (persistent)' : ''}`);
        }
        return cached.data;
    }

    // 4. Check if there's already a pending request for this key
    if (pendingRequests.has(key)) {
        if (import.meta.env.DEV) {
            console.log(`⏳ Waiting for pending request: ${key}`);
        }
        return pendingRequests.get(key);
    }

    // 5. Create new request and store as pending
    const requestPromise = fetcher().then(data => {
        // Cache valid data
        if (data) {
            const cacheEntry = { data, timestamp: Date.now(), version };
            CACHE_STORE.set(key, cacheEntry);

            if (persist) {
                try {
                    localStorage.setItem(storageKey, JSON.stringify(cacheEntry));
                } catch (e) {
                    console.warn('Cache write error:', e);
                }
            }
        }
        // Remove from pending
        pendingRequests.delete(key);
        return data;
    }).catch(error => {
        // Remove from pending on error
        pendingRequests.delete(key);
        throw error;
    });

    pendingRequests.set(key, requestPromise);
    return requestPromise;
};

/**
 * Invalidate cache entries matching a pattern
 * @param {string} pattern - Pattern to match cache keys (supports wildcards with *)
 */
export const invalidateCache = (pattern = null) => {
    if (!pattern) {
        CACHE_STORE.clear();
        // Clear all pm_cache items from localStorage
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('pm_cache_')) {
                localStorage.removeItem(key);
            }
        });
        return;
    }

    const regex = new RegExp(pattern.replace(/\*/g, '.*'));
    for (const key of CACHE_STORE.keys()) {
        if (regex.test(key)) {
            CACHE_STORE.delete(key);
            localStorage.removeItem(`pm_cache_${key}`);
        }
    }

    // Also check localStorage keys that might not be in memory
    Object.keys(localStorage).forEach(key => {
        if (key.startsWith('pm_cache_')) {
            const originalKey = key.replace('pm_cache_', '');
            if (regex.test(originalKey)) {
                localStorage.removeItem(key);
            }
        }
    });
};

/**
 * Get cache entry without checking TTL (for debugging)
 */
export const getCacheEntry = (key) => {
    return CACHE_STORE.get(key);
};

/**
 * Check if a cache entry exists and is valid
 */
export const isCacheValid = (key, ttl = DEFAULT_TTL) => {
    const cached = CACHE_STORE.get(key);
    return cached && (Date.now() - cached.timestamp < ttl);
};

// Utility functions
export const apiUtils = {
    // Check if user is authenticated
    isAuthenticated: () => {
        return !!getAuthToken();
    },

    // Get current user from localStorage
    getCurrentUser: () => {
        const userStr = localStorage.getItem('currentUser');
        return userStr ? JSON.parse(userStr) : null;
    },

    // Check if current user has role
    hasRole: (role) => {
        const user = apiUtils.getCurrentUser();
        return user && user.role === role;
    },

    // Check if current user is admin
    isAdmin: () => {
        return apiUtils.hasRole('admin');
    },

    // Clear auth data
    clearAuth: () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUser');
        window.dispatchEvent(new CustomEvent('auth-token-change'));
    }
};
